<template>
  <basic-block :value="value" :mode="mode" :state.sync="state">
    <template v-slot:main>
      <ShapeControlHandle
        v-if="displayHandler"
        class="shape-arrowarrow-180-deg-deg-control-handler-x"
        :x="radius"
        :x-range="[0, (height - (thickHead - thickBody) / 2) / 2]"
        :y="0"
        :flipX="value.flip.x"
        :flipY="value.flip.y"
        :angle="value.rotation"
        @update:x="x => (value.data.controls.radius = x / ((height - (thickHead - thickBody) / 2) / 2))" />
      <ShapeControlHandle
        v-if="displayHandler"
        class="shape-arrow-180-deg-control-handler-x"
        :x="slope"
        :x-range="[width * 0.25, Math.min(width, headWidth)]"
        :y="0"
        :flipX="value.flip.x"
        :flipY="value.flip.y"
        :angle="value.rotation"
        @update:x="updateSlope" />
      <ShapeControlHandle
        v-if="displayHandler"
        class="shape-arrow-180-deg-control-handler-x"
        :x="headWidth"
        :x-range="[width * 0.51, width]"
        :y="0"
        :flipX="value.flip.x"
        :flipY="value.flip.y"
        :angle="value.rotation"
        @update:x="updateHeadWidth" />
      <ShapeControlHandle
        v-if="displayHandler"
        class="shape-arrow-180-deg-control-handler-y"
        :x="width"
        :y="thickHead"
        :y-range="[5, height]"
        :flipX="value.flip.x"
        :flipY="value.flip.y"
        :angle="value.rotation"
        @update:y="y => (value.data.controls.thickHead = y / height)" />
      <ShapeControlHandle
        v-if="displayHandler"
        class="shape-arrow-180-deg-control-handler-y"
        :x="width"
        :y="height - thickBody"
        :y-range="[Math.max(height * 0.51, thickHead), height - 5]"
        :flipX="value.flip.x"
        :flipY="value.flip.y"
        :angle="value.rotation"
        @update:y="updateThickBody" />
      <svg
        class="shape-arrow-180-deg"
        style="vertical-align: top"
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        :viewBox="`0 0 ${width} ${height}`">
        <defs>
          <path
            :id="`path-${id}`"
            :d="`
                M ${headWidth} ${thickHead / 2}
                L ${slope} 0
                L ${slope} ${(thickHead - thickBody) / 2}
                L ${radius} ${(thickHead - thickBody) / 2}
                A ${radius} ${radius} 0 0 0 0 ${(thickHead - thickBody) / 2 + radius}
                L 0 ${(thickHead - thickBody) / 2 + radius}
                L 0 ${height - radius}
                A ${radius} ${radius} 0 0 0 ${radius} ${height}
                L ${radius} ${height}
                L ${width} ${height}
                L ${width} ${height - thickBody}
                L ${thickBody + innerRadius} ${height - thickBody}
                A ${innerRadius} ${innerRadius} 0 0 1 ${thickBody} ${height - thickBody - innerRadius}
                L ${thickBody} ${height - thickBody - innerRadius}
                L ${thickBody} ${(thickHead + thickBody) / 2 + innerRadius}
                A ${innerRadius} ${innerRadius} 0 0 1 ${thickBody + innerRadius} ${(thickHead + thickBody) / 2}
                L ${thickBody + innerRadius} ${(thickHead + thickBody) / 2}
                L ${slope} ${(thickHead + thickBody) / 2}
                L ${slope} ${thickHead}
                L ${headWidth} ${thickHead / 2}
              `"
            :style="value.data.style"
            stroke-linecap="round" />
          <clipPath :id="`clip-${id}`">
            <use :xlink:href="`#path-${id}`" />
          </clipPath>
        </defs>
        <g>
          <use :xlink:href="`#path-${id}`" :clip-path="`url(#clip-${id})`" />
        </g>
      </svg>
    </template>
  </basic-block>
</template>

<script>
import BasicBlock from "../_components/containers/BasicBlock.vue"
import ShapeControlHandle from "../_components/utils/ShapeControlHandle.vue"
import { BlockSelectionState } from "../_models/blockSelectionState";
export default {
  name: "BlockShapeArrow",
  components: {
    BasicBlock,
    ShapeControlHandle
  },
  props: ["value", "mode"],
  methods: {
    updateHeadWidth(headWidthPx) {
      const diff = this.value.data.controls.headWidth - (headWidthPx / this.width)
      this.value.data.controls.slope -= diff
      this.value.data.controls.headWidth = headWidthPx / this.width
    },
    updateSlope(value) {
      const slopePx = Math.round(value)
      this.value.data.controls.slope = slopePx / this.width
    },
    updateThickBody(value) {
      const thickBodyPx = Math.round(value)
      this.value.data.controls.thickBody = (this.height - thickBodyPx) / this.height
    },
    calculateSlopeOnArrowResize(newWidth, oldWidth) {
      const slopePx = this.value.data.controls.slope * oldWidth
      const distanceFromRight = oldWidth - slopePx
      const newSlopePercentage = (newWidth - distanceFromRight) / newWidth

      this.value.data.controls.slope = newSlopePercentage
    },
    calculateHeadWidthOnArrowResize(newWidth, oldWidth) {
      const headWidthPx = this.value.data.controls.headWidth * oldWidth
      const distanceFromRight = oldWidth - headWidthPx
      const newHeadWidthPercentage = (newWidth - distanceFromRight) / newWidth

      this.value.data.controls.headWidth = newHeadWidthPercentage
    }
  },
  data() {
    return {
      state: BlockSelectionState.notSelected
    }
  },
  computed: {
    displayHandler() {
      return this.state == BlockSelectionState.primarySelected && this.mode === "edit"
    },
    id() {
      return this.value.id
    },
    width() {
      return this.value.size.width
    },
    height() {
      return this.value.size.height
    },
    radius() {
      return this.value.data.controls.radius * ((this.height - (this.thickHead - this.thickBody) / 2) / 2)
    },
    innerRadius() {
      return Math.max(0, this.radius - this.thickBody)
    },
    slope() {
      return this.width * this.value.data.controls.slope
    },
    thickHead() {
      return this.height * this.value.data.controls.thickHead
    },
    thickBody() {
      return this.height * this.value.data.controls.thickBody
    },
    headWidth() {
      return this.width * this.value.data.controls.headWidth
    }
  },
  watch: {
    width(newWidth, oldWidth) {
      if (!newWidth) return

      this.calculateSlopeOnArrowResize(newWidth, oldWidth)
      this.calculateHeadWidthOnArrowResize(newWidth, oldWidth)
    },
    height(newHeight, oldHeight) {
      if (!newHeight) return

      const thickBodyPx = this.value.data.controls.thickBody * oldHeight
      const newThickPercentage = thickBodyPx / newHeight

      this.value.data.controls.thickBody = newThickPercentage
    }
  }
}
</script>
